/**
 * Utility functions for exporting purchase data from the app
 */

/**
 * Export purchase history to CSV format
 * @param {Array} purchases - Array of purchase objects
 * @returns {string} CSV string
 */
export const exportToCSV = (purchases) => {
  if (!purchases || purchases.length === 0) {
    return '';
  }
  
  // Define CSV headers
  const headers = [
    'Item',
    'Amount',
    'Date',
    'Mood',
    'Usage Frequency',
    'Still Happy',
    'Would Buy Again',
    'Guilt Score'
  ];
  
  // Convert purchase objects to CSV rows
  const rows = purchases.map(purchase => {
    const date = new Date(purchase.date).toLocaleDateString();
    return [
      `"${purchase.item || ''}"`, // Quote item in case it contains commas
      parseFloat(purchase.amount || 0).toFixed(2),
      date,
      purchase.mood || '',
      purchase.usageFrequency || '',
      purchase.stillHappy || '',
      purchase.buyAgain || '',
      purchase.guiltScore
    ].join(',');
  });
  
  // Combine headers and rows
  return [headers.join(','), ...rows].join('\n');
};

/**
 * Export purchase history to JSON format
 * @param {Array} purchases - Array of purchase objects
 * @returns {string} JSON string
 */
export const exportToJSON = (purchases) => {
  if (!purchases || purchases.length === 0) {
    return '';
  }
  
  return JSON.stringify(purchases, null, 2);
};

/**
 * Export data to a downloadable file
 * @param {string} content - File content
 * @param {string} fileName - File name
 * @param {string} contentType - MIME type of the file
 */
export const downloadFile = (content, fileName, contentType) => {
  const blob = new Blob([content], { type: contentType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * Export purchase data to various formats
 * @param {Array} purchases - Array of purchase objects
 * @param {string} format - Export format (csv, json)
 */
export const exportPurchaseData = (purchases, format = 'csv') => {
  if (!purchases || purchases.length === 0) {
    return;
  }
  
  const timestamp = new Date().toISOString().split('T')[0];
  
  switch (format.toLowerCase()) {
    case 'csv':
      const csvContent = exportToCSV(purchases);
      downloadFile(csvContent, `guilt-gauge-purchases-${timestamp}.csv`, 'text/csv');
      break;
    case 'json':
      const jsonContent = exportToJSON(purchases);
      downloadFile(jsonContent, `guilt-gauge-purchases-${timestamp}.json`, 'application/json');
      break;
    default:
      throw new Error(`Unsupported export format: ${format}`);
  }
};

/**
 * Create a spending report for a specific time period
 * @param {Array} purchases - Array of purchase objects
 * @param {string} period - Time period (month, year, all)
 * @returns {Object} Spending report
 */
export const createSpendingReport = (purchases, period = 'month') => {
  if (!purchases || purchases.length === 0) {
    return null;
  }
  
  const now = new Date();
  let filteredPurchases;
  
  // Filter purchases based on time period
  switch (period) {
    case 'month':
      filteredPurchases = purchases.filter(purchase => {
        const purchaseDate = new Date(purchase.date);
        return purchaseDate.getMonth() === now.getMonth() &&
               purchaseDate.getFullYear() === now.getFullYear();
      });
      break;
    case 'year':
      filteredPurchases = purchases.filter(purchase => {
        const purchaseDate = new Date(purchase.date);
        return purchaseDate.getFullYear() === now.getFullYear();
      });
      break;
    case 'all':
    default:
      filteredPurchases = [...purchases];
      break;
  }
  
  // Calculate spending metrics
  const totalSpent = filteredPurchases.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const avgSpend = totalSpent / filteredPurchases.length;
  const avgGuilt = filteredPurchases.reduce((sum, p) => sum + p.guiltScore, 0) / filteredPurchases.length;
  
  // Group by mood
  const spendingByMood = filteredPurchases.reduce((acc, purchase) => {
    const mood = purchase.mood || 'unknown';
    acc[mood] = (acc[mood] || 0) + parseFloat(purchase.amount || 0);
    return acc;
  }, {});
  
  // Get high guilt purchases
  const highGuiltPurchases = filteredPurchases
    .filter(p => p.guiltScore > 70)
    .sort((a, b) => parseFloat(b.amount) - parseFloat(a.amount));
  
  return {
    period,
    totalSpent,
    avgSpend,
    avgGuilt,
    purchaseCount: filteredPurchases.length,
    spendingByMood,
    highGuiltPurchases: highGuiltPurchases.slice(0, 5) // Top 5 highest amount
  };
};

/**
 * Generate a printable spending report in HTML format
 * @param {Array} purchases - Array of purchase objects
 * @param {string} period - Time period (month, year, all)
 * @returns {string} HTML content
 */
export const generateSpendingReportHTML = (purchases, period = 'month') => {
  const report = createSpendingReport(purchases, period);
  if (!report) {
    return '<p>No data available</p>';
  }
  
  const periodText = period === 'month' ? 'This Month' : period === 'year' ? 'This Year' : 'All Time';
  
  // Format currency
  const formatCurrency = (amount) => `$${parseFloat(amount).toFixed(2)}`;
  
  return `
    <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px;">
      <h1 style="color: #333; text-align: center;">Guilt Gauge Spending Report</h1>
      <h2 style="color: #666; text-align: center;">${periodText}</h2>
      
      <div style="background-color: #f5f5f5; border-radius: 10px; padding: 20px; margin-bottom: 20px;">
        <h3 style="margin-top: 0;">Summary</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <tr>
            <td style="padding: 8px 0; border-bottom: 1px solid #ddd;">Total Spent:</td>
            <td style="padding: 8px 0; border-bottom: 1px solid #ddd; text-align: right; font-weight: bold;">${formatCurrency(report.totalSpent)}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; border-bottom: 1px solid #ddd;">Number of Purchases:</td>
            <td style="padding: 8px 0; border-bottom: 1px solid #ddd; text-align: right;">${report.purchaseCount}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; border-bottom: 1px solid #ddd;">Average Spend per Purchase:</td>
            <td style="padding: 8px 0; border-bottom: 1px solid #ddd; text-align: right;">${formatCurrency(report.avgSpend)}</td>
          </tr>
          <tr>
            <td style="padding: 8px 0; border-bottom: 1px solid #ddd;">Average Guilt Score:</td>
            <td style="padding: 8px 0; border-bottom: 1px solid #ddd; text-align: right;">${report.avgGuilt.toFixed(1)}</td>
          </tr>
        </table>
      </div>
      
      <div style="background-color: #f5f5f5; border-radius: 10px; padding: 20px; margin-bottom: 20px;">
        <h3 style="margin-top: 0;">Spending by Mood</h3>
        <table style="width: 100%; border-collapse: collapse;">
          <tr style="background-color: #eee;">
            <th style="padding: 8px; text-align: left; border-bottom: 2px solid #ddd;">Mood</th>
            <th style="padding: 8px; text-align: right; border-bottom: 2px solid #ddd;">Amount</th>
          </tr>
          ${Object.entries(report.spendingByMood).map(([mood, amount]) => `
            <tr>
              <td style="padding: 8px; border-bottom: 1px solid #ddd; text-transform: capitalize;">${mood}</td>
              <td style="padding: 8px; border-bottom: 1px solid #ddd; text-align: right;">${formatCurrency(amount)}</td>
            </tr>
          `).join('')}
        </table>
      </div>
      
      ${report.highGuiltPurchases.length > 0 ? `
        <div style="background-color: #f5f5f5; border-radius: 10px; padding: 20px;">
          <h3 style="margin-top: 0;">Top High-Guilt Purchases</h3>
          <table style="width: 100%; border-collapse: collapse;">
            <tr style="background-color: #eee;">
              <th style="padding: 8px; text-align: left; border-bottom: 2px solid #ddd;">Item</th>
              <th style="padding: 8px; text-align: right; border-bottom: 2px solid #ddd;">Amount</th>
              <th style="padding: 8px; text-align: right; border-bottom: 2px solid #ddd;">Guilt Score</th>
            </tr>
            ${report.highGuiltPurchases.map(purchase => `
              <tr>
                <td style="padding: 8px; border-bottom: 1px solid #ddd;">${purchase.item}</td>
                <td style="padding: 8px; border-bottom: 1px solid #ddd; text-align: right;">${formatCurrency(purchase.amount)}</td>
                <td style="padding: 8px; border-bottom: 1px solid #ddd; text-align: right;">${purchase.guiltScore}</td>
              </tr>
            `).join('')}
          </table>
        </div>
      ` : ''}
      
      <div style="margin-top: 40px; text-align: center; color: #888; font-size: 14px;">
        <p>Generated by Guilt Gauge on ${new Date().toLocaleDateString()}</p>
        <p>Shop smarter, feel better.</p>
      </div>
    </div>
  `;
};
